import { GetStaticPaths, GetStaticProps } from 'next';

import ApiService from '@/lib/api';
import { CaseStudy, CaseStudyCard, CustomPage } from '@/types/pages';
import { componentSerializer } from '@/utils/componentSerializer';
import BlockContent from '@sanity/block-content-to-react';
import { imageUrlBuilder } from '@/utils/helpers';
import { withSiteName } from '@/utils/pageTitle';
import MainLayout, { OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH } from '../../layouts/MainLayout';
import CaseStudyCardComponent from '@/components/CaseStudies/CaseStudyCard';
import CaseStudyCta from '@/components/CaseStudyCta';
import Link from 'next/link';
import React from 'react';
import { useRouter } from 'next/router';
import contentStyles from './content.module.css';
import gutterStyles from '@/styles/gutter.module.css';
import LoadingIndicator from '@/components/LoadingIndicator';

// Renders a stat value's rich text inline (no block <p>), applying inline color marks
const statValueSerializers = {
  types: {
    block: (props: { children?: React.ReactNode }) => <>{props.children}</>,
  },
  marks: {
    color: ({ mark, children }: { mark?: { hex?: string }; children: React.ReactNode }) => (
      <span style={mark?.hex ? { color: mark.hex } : undefined}>{children}</span>
    ),
    strong: ({ children }: { children: React.ReactNode }) => <strong>{children}</strong>,
    em: ({ children }: { children: React.ReactNode }) => <em>{children}</em>,
    underline: ({ children }: { children: React.ReactNode }) => (
      <span style={{ textDecoration: 'underline' }}>{children}</span>
    ),
    'strike-through': ({ children }: { children: React.ReactNode }) => (
      <span style={{ textDecoration: 'line-through' }}>{children}</span>
    ),
  },
};

interface CaseStudyPageProps {
  study: CaseStudy | null;
  relatedStudies: CaseStudyCard[];
  menuData: unknown;
  footer: unknown;
  slug: string;
  ctaTitle?: string;
  ctaButtonTitle?: string;
  ctaAction?: string;
  ctaBgColor?: { hex: string };
  ctaTextColor?: { hex: string };
}

export const getStaticPaths: GetStaticPaths = async () => {
  const api = new ApiService();
  const paths = await api.getCaseStudyPaths();

  return {
    paths: paths.filter((p) => p.slug).map((p) => ({ params: { slug: p.slug } })),
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) => {
  const slug = params?.slug as string;
  const api = new ApiService();
  const [study, menuData, miscData, pageData] = await Promise.all([
    api.getCaseStudyBySlug(slug, preview),
    api.getMenuData(),
    api.getMiscData(),
    api.getCaseStudiesPageData(preview),
  ]);

  if (!study) {
    return { notFound: true };
  }

  // Fetch 4, filter out current slug client-side to guarantee 3 results
  const allCards = await api.getCaseStudiesCards(0, 4);
  const relatedStudies = allCards.filter((c) => c.slug !== slug).slice(0, 3);

  const { footer } = miscData;

  return {
    props: {
      study,
      relatedStudies,
      menuData,
      footer,
      slug,
      ctaTitle: pageData?.ctaTitle ?? null,
      ctaButtonTitle: pageData?.ctaButtonTitle ?? null,
      ctaAction: pageData?.ctaAction ?? null,
      ctaBgColor: pageData?.ctaBgColor ?? null,
      ctaTextColor: pageData?.ctaTextColor ?? null,
    },
    revalidate: 3600,
  };
};

const CaseStudyPage: React.FC<CaseStudyPageProps> = ({
  study,
  relatedStudies,
  menuData,
  footer,
  slug,
  ctaTitle,
  ctaButtonTitle,
  ctaAction,
  ctaBgColor,
  ctaTextColor,
}) => {
  const router = useRouter();
  const statCardsRef = React.useRef<(HTMLDivElement | null)[]>([]);

  React.useEffect(() => {
    const cards = statCardsRef.current.filter(Boolean) as HTMLDivElement[];
    if (cards.length === 0) return;
    cards.forEach((el) => (el.style.minHeight = ''));
    const max = Math.max(...cards.map((el) => el.offsetHeight));
    const finalHeight = Math.max(max, 180);
    cards.forEach((el) => (el.style.minHeight = `${finalHeight}px`));
  }, [study]);

  if (router.isFallback) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingIndicator text="Loading" />
      </div>
    );
  }

  if (!study) {
    return null;
  }

  const heroSrc = study.heroImage?.image?.asset
    ? imageUrlBuilder
        .image(study.heroImage.image.asset)
        .width(1200)
        .height(1000)
        .fit('crop')
        .auto('format')
        .quality(80)
        .url()
    : null;

  const contentBlocks = componentSerializer(
    {
      _id: study._id,
      title: study.title,
      content: study.content as unknown as CustomPage['content'],
    },
    { narrow: true, wideGutter: true },
  );

  const teamBlock = (study.content as unknown as Array<Record<string, unknown>>)?.find(
    (c) => c._type === 'caseStudyTeam',
  );
  const SectionHeadingTag = (teamBlock?.headingLevel || 'h2') as keyof React.JSX.IntrinsicElements;

  const stats = study.stats ?? [];
  const statColsClass =
    stats.length === 1
      ? 'mx-auto max-w-xs grid-cols-1'
      : stats.length === 2
        ? 'mx-auto max-w-xl grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1 sm:grid-cols-3';

  // An editor-set SEO title ships verbatim: the Studio counter measures that field against
  // 30-60 characters, so appending the brand here would make the counter lie. The suffix is
  // only for the composed fallback, which no counter covers (FOX2-77).
  const metaTitle = study.seo?.title || withSiteName(`${study.client} — ${study.title}`);
  const metaDescription = study.seo?.description || study.excerpt || undefined;
  const ogUrl = `https://foxbox.com/case-studies/${slug}`;

  // Social crawlers need their own crop: the hero is served at 1200x1000 for the page,
  // which LinkedIn and X render as a small thumbnail. When a study ships without a hero,
  // MainLayout's branded fallback takes over.
  const ogImage = study.heroImage?.image?.asset
    ? imageUrlBuilder
        .image(study.heroImage.image.asset)
        .width(OG_IMAGE_WIDTH)
        .height(OG_IMAGE_HEIGHT)
        .fit('crop')
        .auto('format')
        .quality(80)
        .url()
    : undefined;

  return (
    <MainLayout
      navIsFixed={false}
      noMargin
      navIsDark={false}
      bgWhite={true}
      ogUrl={ogUrl}
      ogImage={ogImage}
      ogImageWidth={ogImage ? OG_IMAGE_WIDTH : undefined}
      ogImageHeight={ogImage ? OG_IMAGE_HEIGHT : undefined}
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      menuData={menuData}
      footer={footer}
    >
      {/* Title band — breadcrumb + hero, full-bleed #f4f4f4 via a-gray-light token */}
      <div className="bg-a-gray-light pt-10 pb-6 sm:pb-10">
        {/* Breadcrumb */}
        <nav className="mb-4 pb-1 text-base text-a-text-secondary sm:mb-0">
          <div className={`mx-auto max-w-a-wide ${gutterStyles.gutter}`}>
            <a href="/case-studies" className="hover:underline">
              Case Studies
            </a>
            {' / '}
            <span>{study.client}</span>
          </div>
        </nav>
        {/* Hero — taller half-and-half (Valtech-style) */}
        <section className={`sm:pb-4 sm:pt-8 ${gutterStyles.gutterRight}`}>
          <div
            className={`mx-auto grid max-w-a-wide grid-cols-1 overflow-hidden ${heroSrc ? 'md:grid-cols-2' : ''}`}
            style={heroSrc ? { minHeight: '34rem' } : undefined}
          >
            <div
              className={`order-2 flex flex-col justify-center pt-6 pb-0 sm:order-1 sm:py-12 ${gutterStyles.gutter}`}
            >
              <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-a-navy">{study.client}</p>
              <h1
                className="text-4xl font-bold leading-tight text-a-navy sm:text-5xl"
                style={study.titleColor?.hex ? { color: study.titleColor.hex } : undefined}
              >
                {study.title}
              </h1>
              {study.excerpt && (
                <p
                  className="mt-5 text-lg leading-relaxed text-a-text-secondary"
                  style={study.excerptColor?.hex ? { color: study.excerptColor.hex } : undefined}
                >
                  {study.excerpt}
                </p>
              )}
            </div>
            {heroSrc && (
              <div className="relative order-1 sm:order-2" style={{ minHeight: '18rem' }}>
                <img
                  src={heroSrc}
                  alt={study.heroImage?.alt || study.title}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            )}
          </div>
        </section>
      </div>
      {/* Meta — industry / offering / technology */}
      {(study.industries?.length > 0 || study.offerings?.length > 0 || study.technologies?.length > 0) && (
        <section className="pt-6 pb-4 sm:pt-10">
          <div className={`mx-auto flex max-w-a-wide flex-col gap-12 sm:flex-row sm:gap-x-32 ${gutterStyles.gutter}`}>
            {study.industries?.length > 0 && (
              <div>
                <h2 className="mt-0 mb-2 text-sm font-semibold uppercase tracking-widest text-a-navy">Industry</h2>
                <ul className="flex flex-col gap-1">
                  {study.industries.map((item) => (
                    <li key={item._id} className="mb-0 text-base font-medium">
                      <Link
                        href={`/case-studies?industry=${item._id}`}
                        className="text-a-navy transition-colors hover:text-orange"
                      >
                        {item.title.trim()}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {study.offerings?.length > 0 && (
              <div>
                <h2 className="mt-0 mb-2 text-sm font-semibold uppercase tracking-widest text-a-navy">Offerings</h2>
                <ul className="flex flex-col gap-1">
                  {study.offerings.map((item) => (
                    <li key={item._id} className="mb-0 text-base font-medium">
                      {item.link ? (
                        <Link href={item.link} className="text-a-navy transition-colors hover:text-orange">
                          {item.title.trim()}
                        </Link>
                      ) : (
                        <span className="text-a-navy">{item.title.trim()}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {study.technologies?.length > 0 && (
              <div>
                <h2 className="mt-0 mb-2 text-sm font-semibold uppercase tracking-widest text-a-navy">Technologies</h2>
                <ul className="flex flex-col gap-1">
                  {[...study.technologies]
                    .sort((a, b) => a.title.trim().localeCompare(b.title.trim(), undefined, { sensitivity: 'base' }))
                    .map((item) => (
                      <li key={item._id} className="mb-0 text-base font-medium">
                        <Link
                          href={`/case-studies?technology=${item._id}`}
                          className="text-a-navy transition-colors hover:text-orange"
                        >
                          {item.title.trim()}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
      {/* Stats band */}
      {stats.length > 0 && (
        <section className="pt-10 pb-2">
          <div className={`mx-auto grid max-w-a-wide gap-6 px-6 sm:px-8 ${statColsClass}`}>
            {stats.map((stat, index) => (
              <div
                key={stat._key}
                ref={(el) => {
                  statCardsRef.current[index] = el;
                }}
                className="flex flex-col justify-center rounded-lg bg-a-off-white px-8 py-5 text-left sm:min-h-[460px] sm:justify-between"
              >
                <p className="break-words text-2xl font-bold leading-tight text-a-text-primary sm:text-3xl lg:text-2-5xl">
                  <BlockContent blocks={stat.value} serializers={statValueSerializers} />
                </p>
                <p className="mb-2 max-w-[200px] text-left text-base text-a-text-secondary">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      <main className={contentStyles.content}>{contentBlocks}</main>
      {relatedStudies?.length > 0 && (
        <section className={`pb-8 ${gutterStyles.gutter}`} style={{ paddingTop: teamBlock ? '5rem' : '2rem' }}>
          <div className="mx-auto max-w-a-wide">
            <SectionHeadingTag className="mb-5 font-bold text-a-navy">More case studies</SectionHeadingTag>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
              {relatedStudies.map((s) => (
                <CaseStudyCardComponent key={s._id} {...s} />
              ))}
            </div>
          </div>
        </section>
      )}
      <CaseStudyCta
        title={ctaTitle ?? undefined}
        buttonTitle={ctaButtonTitle ?? undefined}
        buttonAction={ctaAction ?? undefined}
        buttonBgColor={ctaBgColor ?? undefined}
        buttonTextColor={ctaTextColor ?? undefined}
      />
    </MainLayout>
  );
};

export default CaseStudyPage;
